import React from 'react';
import { ThemeProvider, Card, Stack, Text, Icon, Spinner, Alert } from '@amzn/orion-components';
import { useControls } from 'src/api/hooks/useControlData';
import type { ControlMetadata } from '@amzn/controllershiphub-typescript-client';
import { useNavigate } from 'react-router-dom';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import { SECTION_CONFIG } from 'src/constants/sectionConfig';
import { ROUTES } from 'src/router/routes';
import './LandingPage.scss';

const slugify = (str: string) => str.replace(/\s/g, '-').toLowerCase();

export default function Landing(): JSX.Element {
  const { data, isLoading, error } = useControls();
  const navigate = useNavigate();
  const { alias } = useUserAlias();
  const controls = data?.controls ?? [];

  return (
    <ThemeProvider brandColors={{ primary: '#0891B2', secondary: '#BE185D' }} defaultMode="light">
      <div className="landing-page">
        {/* Greeting bar */}
        <div className="greeting-bar">
          <div
            className="greeting-bar__content"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <div style={{ marginBottom: '16px' }}>
              <Text as="p" fontSize="base" fontWeight="normal">
                Hello,{' '}
                <span style={{ color: 'var(--color-accent, #0891B2)' }}>{alias || 'there'}</span>!
                What do you want to know today?
              </Text>
            </div>
            <h2
              style={{
                fontSize: '36px',
                fontWeight: 700,
                color: '#1a1a1a',
                margin: '4px 0 12px',
              }}
            >
              <span className="hub-animate">Controllership Hub</span>
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              Designed with a simple but powerful vision: make controllership easier, smarter, and
              faster for every finance team at Amazon.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error">Error loading controls: {error.message}</Alert>
        ) : (
          <div className="landing-body">
            {/* Main content — control sections */}
            <div className="landing-main">
              {SECTION_CONFIG.map(({ icon, headerText, controlType }) => {
                const sectionControls = controls.filter(
                  (c: ControlMetadata) => c.controlType === controlType
                );
                if (sectionControls.length === 0) return null;
                return (
                  <div
                    key={headerText}
                    id={headerText.replace(/\s/g, '-').toLowerCase()}
                    className="control-section"
                  >
                    <Text as="h2" fontSize="lg" fontWeight="semibold">
                      <Icon name={icon} size="md" color="#0891B2" /> {headerText}
                    </Text>
                    <div className="control-grid">
                      {sectionControls.map((item: ControlMetadata) => (
                        <Card
                          key={item.controlId}
                          elevation="elevated"
                          variant="subtle"
                          padding="lg"
                          border="corners"
                          borderColor="default"
                          cornerLength="120px"
                          glow="hover"
                          glowStyle="rainbow"
                          onClick={() =>
                            item.controlId?.toLowerCase().includes('procurement')
                              ? navigate(ROUTES.PROCUREMENT.BASE)
                              : navigate(`/${slugify(item.controlType!)}/${item.controlId}`)
                          }
                        >
                          <Stack direction="horizontal" gap="sm" align="start">
                            <div className="control-card__icon">
                              <Icon name="document" size="sm" />
                            </div>
                            <Stack direction="vertical" gap="xs">
                              <Text fontSize="base" fontWeight="normal">
                                {item.controlName}
                              </Text>
                              <Text fontSize="xs" variant="secondary">
                                {item.controlSubTitle}
                              </Text>
                            </Stack>
                          </Stack>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}
